import { Loader2, CheckCircle2, AlertCircle, FileText } from 'lucide-react'
import { cn } from '@/lib/utils'
import type { Document } from '@/types/api'

interface ProcessingQueueProps {
  documents: Document[]
}

/**
 * Vertical list of documents currently moving through the ingestion
 * pipeline. Auto-hides when the parent passes an empty list. Each row
 * shows: file icon, filename, status label, animated indicator. The
 * parent (OverviewPage) prunes completed/failed entries on a delay so the
 * user sees the "completed" flash before the row disappears.
 */
export function ProcessingQueue({ documents }: ProcessingQueueProps) {
  if (documents.length === 0) return null

  return (
    <div
      data-testid="processing-queue"
      className="rounded-lg border border-border-default bg-bg-elevated overflow-hidden"
      role="status"
      aria-live="polite"
    >
      <div className="flex items-center gap-2 px-4 py-2 border-b border-border-default text-sm text-text-secondary">
        <Loader2 size={14} className="animate-spin text-primary" />
        Processing {documents.length} document{documents.length === 1 ? '' : 's'}…
      </div>
      <ul className="divide-y divide-border-default">
        {documents.map((doc) => (
          <ProcessingRow key={doc.id} doc={doc} />
        ))}
      </ul>
    </div>
  )
}

interface ProcessingRowProps {
  doc: Document
}

function ProcessingRow({ doc }: ProcessingRowProps) {
  const inflight =
    doc.status === 'uploading' ||
    doc.status === 'pending' ||
    doc.status === 'processing'

  return (
    <li className="flex items-center gap-3 px-4 py-3 text-sm">
      <FileText size={16} className="text-text-tertiary shrink-0" />
      <span className="flex-1 truncate text-text-primary">{doc.name}</span>
      <span
        className={cn(
          'flex items-center gap-1.5 text-xs',
          doc.status === 'failed' && 'text-error',
          doc.status === 'completed' && 'text-success',
          inflight && 'text-text-secondary'
        )}
      >
        {inflight && <Loader2 size={12} className="animate-spin" />}
        {doc.status === 'completed' && <CheckCircle2 size={12} />}
        {doc.status === 'failed' && <AlertCircle size={12} />}
        <span className="capitalize">{labelFor(doc.status)}</span>
      </span>
    </li>
  )
}

function labelFor(status: Document['status']): string {
  switch (status) {
    case 'uploading':
      return 'Uploading'
    case 'pending':
      return 'Queued'
    case 'processing':
      return 'Reading'
    case 'completed':
      return 'Done'
    case 'failed':
      return 'Failed'
  }
}
